<template>
  <div>
    <v-dialog
      v-model="display"
      :width="width"
      persistent
      lazy
      full-width
      :disabled="disabled"
    >
      <v-text-field
        slot="activator"
        :label="label"
        :value="formattedDatetime"
        box
        :disabled="disabled"
        :error-messages="getErrors(validator)"
        append-icon="schedule"
        readonly
        @blur="validator && validator.$touch()"
      />
      <v-card>
        <v-card-text style="padding: 0; height: 378px">
          <v-time-picker
            ref="timer"
            v-model="timePart"
            :format="timePickerFormat"
            :min="computedMin"
            :max="computedMax"
            :allowed-hours="allowedHours"
            :allowed-minutes="allowedMinutes"
            full-width
            class="v-time-picker-custom"
            scrollable
            actions
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <main-button :text="cancelText === 'Cancel' ? adminText('shared.cancel', 'Cancel') : cancelText" flat @click.native="cancelHandler" />
          <main-button :text="okText === 'OK' ? adminText('shared.ok', 'OK') : okText" depressed @click.native="okHandler" />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import AdminContext from '@/admin-i18n/context'
import moment from 'moment'
import { defaultMessages, getValidationErrors } from './../../../utils'

const DEFAULT_TIME_FORMAT = 'HH:mm'

export default {
  mixins: [AdminContext],
  name: 'time-field',
  props: {
    validator: {
      type: Object,
      default: null,
    },
    errorMessages: {
      type: Object,
      default: null,
    },
    value: {
      type: [Date, String],
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    width: {
      type: Number,
      default: 320,
    },
    format: {
      type: String,
      default: 'HH:mm',
    },
    timePickerFormat: {
      type: String,
      default: '24hr',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    cancelText: {
      type: String,
      default: 'Cancel',
    },
    okText: {
      type: String,
      default: 'OK',
    },
    min: {
      type: String,
      default: undefined,
    },
    max: {
      type: String,
      default: undefined,
    },
  },
  data() {
    this.value && this.$emit('input', this.value)

    return {
      display: false,
      timeSelected: false,
      selectedDatetime: this.value,
    }
  },
  computed: {
    timePart: {
      get() {
        const time = this.selectedDatetime
          ? this.selectedDatetime
          : moment().format(DEFAULT_TIME_FORMAT)

        return time
      },
      set(val) {
        this.timeSelected = true

        this.selectedDatetime = val
      },
    },
    formattedDatetime() {
      return this.value ? this.value : ''
    },
    computedMin() {
      if (!this.min) return undefined
      const parsed = moment(this.min, ['HH:mm', 'H:mm', 'HH:m', 'H:m'], true)

      return parsed.isValid() ? parsed.format(DEFAULT_TIME_FORMAT) : undefined
    },
    computedMax() {
      if (!this.max) return undefined
      const parsed = moment(this.max, ['HH:mm', 'H:mm', 'HH:m', 'H:m'], true)

      return parsed.isValid() ? parsed.format(DEFAULT_TIME_FORMAT) : undefined
    },
    minParts() {
      if (!this.computedMin) return undefined
      const m = moment(this.computedMin, DEFAULT_TIME_FORMAT)

      return { hour: m.hour(), minute: m.minute() }
    },
    maxParts() {
      if (!this.computedMax) return undefined
      const m = moment(this.computedMax, DEFAULT_TIME_FORMAT)

      return { hour: m.hour(), minute: m.minute() }
    },
    allowedHours() {
      const self = this

      return function (hour) {
        if (self.minParts && hour < self.minParts.hour) return false
        if (self.maxParts && hour > self.maxParts.hour) return false

        return true
      }
    },
    allowedMinutes() {
      const self = this

      return function (minute) {
        const currentHour = moment(self.timePart, DEFAULT_TIME_FORMAT).hour()

        if (self.minParts && currentHour === self.minParts.hour && minute < self.minParts.minute) {
          return false
        }

        if (self.maxParts && currentHour === self.maxParts.hour && minute > self.maxParts.minute) {
          return false
        }

        return true
      }
    },
  },
  watch: {
    display(val) {
      if (val) {
        this.selectedDatetime = this.selectedDatetime
          ? this.selectedDatetime
          : this.computedMin || this.computedMax || moment().format(DEFAULT_TIME_FORMAT)
      }
    },
  },
  methods: {
    getTime(input) {
      if (moment(this.selectedDatetime).startOf('day').isSame(moment(input).startOf('day'))) {
        return moment(input).format(DEFAULT_TIME_FORMAT)
      }

      return undefined
    },
    okHandler() {
      this.display = false
      this.selectedDatetime = this.selectedDatetime
        ? this.selectedDatetime
        : this.min || this.max || moment().format(DEFAULT_TIME_FORMAT)

      // Clamp final selection within [min, max]
      const selected = moment(this.selectedDatetime, DEFAULT_TIME_FORMAT)
      if (this.computedMin) {
        const minM = moment(this.computedMin, DEFAULT_TIME_FORMAT)
        if (selected.isBefore(minM)) {
          this.selectedDatetime = this.computedMin
        }
      }
      if (this.computedMax) {
        const maxM = moment(this.computedMax, DEFAULT_TIME_FORMAT)
        if (moment(this.selectedDatetime, DEFAULT_TIME_FORMAT).isAfter(maxM)) {
          this.selectedDatetime = this.computedMax
        }
      }
      this.$emit('input', this.selectedDatetime)
    },
    getErrors: function (field) {
      return getValidationErrors({
        ...this.adminValidationMessages(defaultMessages),
        ...this.adminFieldValidationMessages(this.errorMessages),
      })(field)
    },
    cancelHandler() {
      this.display = false
    },
  },
}
</script>

<style>
.v-time-picker-title {
  height: 56px !important;
}
.v-input--has-state .v-input__slot:before {
  border-color: rgb(219, 56, 76) !important;
}
</style>

<style scoped>
.v-card {
  box-shadow: none;
}
</style>
